import { SEO } from "@/components/seo";
import { siteConfig } from "@/config/website.config";
import { broker } from "@/config/broker.config";

export function DisclaimerPage() {
  const lastUpdated = "25 February 2026";

  return (
    <>
      <SEO
        title="Disclaimer"
        description="Important disclaimers regarding the services, calculators, and information provided by Path Financial Solutions."
        url="/disclaimer"
      />

      {/* Hero */}
      <section className="bg-cream py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <p className="section-label mb-4">Legal</p>
          <h1 className="font-serif text-4xl md:text-5xl mb-4">Disclaimer</h1>
          <p className="text-muted-foreground">Last updated: {lastUpdated}</p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="prose prose-lg max-w-none">
            {/* General Disclaimer */}
            <div className="mb-12">
              <h2 className="font-serif text-2xl mb-4">General Disclaimer</h2>
              <p className="text-muted-foreground mb-4">
                The information provided on this website by {siteConfig.appName} (ABN {siteConfig.business.abn},
                Credit Representative 373517, authorised under Australian Credit Licence {siteConfig.business.acl})
                is of a general nature only. It has been prepared without taking into account your particular
                objectives, financial situation or needs.
              </p>
              <p className="text-muted-foreground mb-4">
                Before acting on any information on this website, you should consider the appropriateness
                of the information having regard to your objectives, financial situation and needs. You should
                seek independent professional advice before making any financial decisions.
              </p>
              <p className="text-muted-foreground">
                {siteConfig.appName} does not provide legal, tax, or financial advice. The content on this
                website is for informational purposes only and should not be relied upon as a substitute
                for professional advice tailored to your individual circumstances.
              </p>
            </div>

            {/* Credit & Broker Services */}
            <div className="mb-12">
              <h2 className="font-serif text-2xl mb-4">Credit & Broker Services</h2>
              <p className="text-muted-foreground mb-4">
                {broker.name} is a Credit Representative (Number 373517) of AFG (Australian Finance Group Ltd),
                which holds Australian Credit Licence {siteConfig.business.acl}. Any credit assistance provided
                is subject to the terms and conditions of AFG's Australian Credit Licence.
              </p>
              <p className="text-muted-foreground mb-4">
                All loan products are subject to the credit provider's terms, conditions, fees, and
                lending criteria. Approval is subject to the lender's assessment of your financial
                situation and is not guaranteed. Your full financial situation will need to be reviewed
                prior to acceptance of any offer or product.
              </p>
              <p className="text-muted-foreground">
                We recommend that you read the relevant Product Disclosure Statement (PDS), terms and
                conditions, and other disclosure documents provided by the credit provider before making
                a decision about any credit product.
              </p>
            </div>

            {/* Calculators & Tools */}
            <div className="mb-12">
              <h2 className="font-serif text-2xl mb-4">Calculators & Tools</h2>
              <p className="text-muted-foreground mb-4">
                The calculators and tools available on this website are provided for illustrative and
                informational purposes only. They produce estimates based on the information you provide
                and generalised assumptions. Results should be used as a guide only and should not be
                relied upon as a basis for making financial decisions.
              </p>
              <p className="text-muted-foreground mb-4">
                The rates, figures, and calculations displayed may contain errors, may not reflect
                current market conditions, and are provided as examples or rough estimates only.
                {" "}{siteConfig.appName} does not accept any responsibility or liability for any loss, damage,
                cost, or expense incurred by any person as a result of relying on the output of these
                calculators, including but not limited to errors in rates, data, or calculations.
              </p>
              <p className="text-muted-foreground">
                Actual loan amounts, repayments, fees, stamp duty, and other figures will vary depending
                on your individual circumstances, the lender, and the product selected. Always consult
                with a qualified mortgage broker or financial professional for accurate and personalised
                advice.
              </p>
            </div>

            {/* Interest Rates & Product Information */}
            <div className="mb-12">
              <h2 className="font-serif text-2xl mb-4">Interest Rates & Product Information</h2>
              <p className="text-muted-foreground mb-4">
                Interest rates, fees, product features, and other information displayed on this website
                are subject to change without notice. While we endeavour to keep information current and
                accurate, we do not warrant or guarantee that the information is complete, accurate,
                or up-to-date at all times.
              </p>
              <p className="text-muted-foreground">
                Rates shown may be indicative only and may differ from the rates offered to you by a
                lender. The availability of products and features may vary. Contact us or the relevant
                lender for the most current rates and product information.
              </p>
            </div>

            {/* Email Communications */}
            <div className="mb-12">
              <h2 className="font-serif text-2xl mb-4">Email Communications</h2>
              <p className="text-muted-foreground mb-4">
                Any rates, estimates, or information provided via email are for general informational
                purposes only and may contain errors or inaccuracies. Email communications do not
                constitute a formal offer, financial advice, or a commitment to lend.
              </p>
              <p className="text-muted-foreground">
                You should not rely solely on information provided via email when making financial
                decisions. Always verify information independently and seek professional advice.
              </p>
            </div>

            {/* Limitation of Liability */}
            <div className="mb-12">
              <h2 className="font-serif text-2xl mb-4">Limitation of Liability</h2>
              <p className="text-muted-foreground mb-4">
                To the maximum extent permitted by law, {siteConfig.appName}, its directors, employees,
                and agents disclaim all liability and responsibility for any direct, indirect, incidental,
                special, consequential, or punitive loss, damage, cost, or expense of any kind arising
                from or connected with:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground mb-4">
                <li>Your use of or reliance on any information, calculators, tools, or content on this website</li>
                <li>Any errors, omissions, or inaccuracies in rates, data, calculations, or other information</li>
                <li>Any financial loss or damage arising from decisions made based on information obtained from this website</li>
                <li>Any interruption, delay, or inability to access this website</li>
              </ul>
              <p className="text-muted-foreground">
                This limitation applies regardless of whether the loss or damage was foreseeable
                or whether {siteConfig.appName} was advised of the possibility of such loss or damage.
              </p>
            </div>

            {/* Third-Party Links */}
            <div className="mb-12">
              <h2 className="font-serif text-2xl mb-4">Third-Party Links</h2>
              <p className="text-muted-foreground">
                This website may contain links to third-party websites. These links are provided for
                your convenience only and do not imply endorsement or approval of the linked site.
                {" "}{siteConfig.appName} is not responsible for the content, accuracy, or practices
                of any third-party website.
              </p>
            </div>

            {/* Contact */}
            <div className="bg-muted rounded-2xl p-8">
              <h2 className="font-serif text-2xl mb-4">Questions?</h2>
              <p className="text-muted-foreground mb-2">
                If you have any questions about this disclaimer, please contact us:
              </p>
              <p className="text-muted-foreground">
                <strong>Email:</strong>{" "}
                <a href={`mailto:${broker.email}`} className="text-accent hover:underline">
                  {broker.email}
                </a>
                <br />
                <strong>Phone:</strong>{" "}
                <a href={`tel:${broker.phone.replace(/\s/g, "")}`} className="text-accent hover:underline">
                  {broker.phone}
                </a>
              </p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
